import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  alpha,
  useTheme,
  Grid,
  Avatar,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import TableComponent from "../admincomponents/Table";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import axios from "axios";
import { useAuth } from "../../../utils/AuthContext";

const GOLD = "#D4AF37";

const CourseAssignment = () => {
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [selectedOccupation, setSelectedOccupation] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const theme = useTheme();
  const { token } = useAuth();

  const fetchAssignedCourses = useCallback(async () => {
    try {
      setIsTableLoading(true);
      const response = await axios.get(
        "https://tdtlworld.com/dss-kaas-backend/lms/admin/recent-course-assignments/",
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const transformedAssignments = response.data.assignments.map(
        (assignment) => ({
          id:
            assignment.id ||
            assignment.assignment_id ||
            `${assignment.user_id}-${assignment.course_id}`,
          student: assignment.user_id,
          course: assignment.course_id,
          studentDetails: {
            user_id: assignment.user_id,
            first_name: assignment.user_name.split(" ")[0],
            last_name: assignment.user_name.split(" ").slice(1).join(" "),
            email: assignment.email,
          },
          courseDetails: {
            course_id: assignment.course_id,
            title: assignment.course_name,
          },
          enrolled_on: assignment.enrolled_on,
          is_approved: assignment.is_approved,
          price: assignment.price,
        }),
      );

      setAssignments(transformedAssignments);
    } catch (err) {
      console.error("Failed to fetch assignments:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch course assignments",
        confirmButtonColor: GOLD,
      });
    } finally {
      setIsTableLoading(false);
    }
  }, [token]);

  const fetchingRef = React.useRef(false);

  useEffect(() => {
    if (!token || fetchingRef.current) return;
    
    const fetchData = async () => {
      fetchingRef.current = true;
      console.log("Courseassignment: Starting fetchData...");
      try {
        setIsTableLoading(true);
        const headers = { Authorization: `Bearer ${token}` };

        const [usersResponse, coursesResponse, occupationsResponse] =
          await Promise.all([
            axios.get("https://tdtlworld.com/dss-kaas-backend/lms/all-users/", {
              headers,
            }),
            axios.get("https://tdtlworld.com/dss-kaas-backend/lms/courses/", {
              headers,
            }),
            axios.get(
              "https://tdtlworld.com/dss-kaas-backend/master/occupations/",
              { headers },
            ),
          ]);

        setUsers(usersResponse.data.users || []);
        setFilteredUsers(usersResponse.data.users || []);
        setCourses(coursesResponse.data.courses || []);
        setOccupations(occupationsResponse.data || []);

        // Inline fetchAssignedCourses logic for safety
        const assignedRes = await axios.get(
          "https://tdtlworld.com/dss-kaas-backend/lms/admin/recent-course-assignments/",
          { headers },
        );
        const transformed = assignedRes.data.assignments.map(a => ({
          id: a.id || a.assignment_id || `${a.user_id}-${a.course_id}`,
          student: a.user_id,
          course: a.course_id,
          studentDetails: { user_id: a.user_id, first_name: a.user_name.split(" ")[0], last_name: a.user_name.split(" ").slice(1).join(" "), email: a.email },
          courseDetails: { course_id: a.course_id, title: a.course_name },
          enrolled_on: a.enrolled_on,
          is_approved: a.is_approved,
          price: a.price,
        }));
        setAssignments(transformed);

      } catch (err) {
        console.error("Courseassignment API error:", err);
      } finally {
        setIsTableLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchData();
  }, [token]);

  useEffect(() => {
    if (selectedOccupation) {
      const filtered = users.filter((user) => {
        const userOccupation = user.occupation
          ? user.occupation.toString().toLowerCase()
          : "";
        const selectedOccupationLower = selectedOccupation
          .toString()
          .toLowerCase();
        return userOccupation === selectedOccupationLower;
      });
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [selectedOccupation, users]);

  const getOccupationName = (occupation) => {
    if (!occupation) return "Not specified";
    if (typeof occupation === "number" || !isNaN(occupation)) {
      const occupationObj = occupations.find(
        (o) => o.id.toString() === occupation.toString(),
      );
      return occupationObj ? occupationObj.name : occupation;
    }
    return occupation;
  };

  const getUserDisplay = (user) => {
    const occupationName = getOccupationName(user.occupation);
    let details = user.district || "";
    return `${user.first_name} ${user.last_name} (${details})`;
  };

  const getUserName = (userId) => {
    const user = users.find((u) => u.user_id.toString() === userId.toString());
    return user ? `${user.first_name} ${user.last_name}` : `User ID: ${userId}`;
  };

  const handleAssign = async () => {
    if (!selectedUsers.length || !selectedCourse) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Selection",
        text: "Please select at least one user and a course",
        confirmButtonColor: GOLD,
      });
      return;
    }

    try {
      setIsModalLoading(true);
      const payload = {
        user_ids: selectedUsers.map((id) => Number(id)),
        course_id: Number(selectedCourse),
      };

      const response = await axios.post(
        "https://tdtlworld.com/dss-kaas-backend/lms/admin/assign-course/",
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.status >= 200 && response.status < 300) {
        await fetchAssignedCourses();
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.message || "Course assigned successfully",
          confirmButtonColor: GOLD,
        });
        setSelectedOccupation("");
        setSelectedUsers([]);
        setSelectedCourse("");
        setShowModal(false);
      } else {
        throw new Error(response.data.message || "Failed to assign course");
      }
    } catch (err) {
      console.error("Assignment error:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Failed to assign course",
        confirmButtonColor: GOLD,
      });
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will delete the assignment.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: GOLD,
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it",
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          setIsTableLoading(true);
          await axios.delete(
            `https://tdtlworld.com/dss-kaas-backend/lms/admin/recent-course-assignments/${id}/`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          await fetchAssignedCourses();
          Swal.fire({
            title: "Deleted!",
            text: "Assignment removed.",
            icon: "success",
            confirmButtonColor: GOLD,
          });
        } catch (err) {
          console.error("Delete error:", err);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to delete assignment",
            confirmButtonColor: GOLD,
          });
        } finally {
          setIsTableLoading(false);
        }
      }
    });
  };

  const filteredAssignments = assignments.filter((a) => {
    const userName = getUserName(a.student).toLowerCase();
    const courseTitle =
      courses.find((c) => c.course_id === a.course)?.title.toLowerCase() || "";
    return (
      userName.includes(searchTerm.toLowerCase()) ||
      courseTitle.includes(searchTerm.toLowerCase())
    );
  });

  const columns = [
    {
      key: "srNo",
      label: "#",
      sortable: false,
      render: (row, index) => (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", fontWeight: 600 }}
        >
          {index + 1}
        </Typography>
      ),
    },
    {
      key: "student",
      label: "Student Name",
      sortable: true,
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: alpha(GOLD, 0.1),
              color: GOLD,
              width: 32,
              height: 32,
              fontSize: "0.8rem",
              fontWeight: 800,
            }}
          >
            {getUserName(row.student).charAt(0)}
          </Avatar>
          <Typography variant="body2" fontWeight={700}>
            {getUserName(row.student)}
          </Typography>
        </Box>
      ),
    },
    {
      key: "course",
      label: "Course Title",
      sortable: true,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {courses.find((c) => Number(c.course_id) === Number(row.course))
            ?.title || `Course ID: ${row.course}`}
        </Typography>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      align: "center",
      render: (row) => (
        <IconButton
          disabled={isTableLoading}
          onClick={() => handleDelete(row.id)}
          sx={{
            color: "error.main",
            bgcolor: alpha(theme.palette.error.main, 0.05),
            "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.1) },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const allOccupations = [
    ...new Set([
      ...occupations.map((occ) => occ.name),
      ...users.map((u) => u.occupation).filter(Boolean),
    ]),
  ].filter(Boolean);

  return (
    <Box>
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              color: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
              fontWeight: 800,
              mb: 0.5,
              fontFamily: "'Outfit', sans-serif",
            }}
          >
            Course{" "}
            <Box component="span" sx={{ color: GOLD }}>
              Assignments
            </Box>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage student enrollments and course access.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingId(null);
            setSelectedOccupation("");
            setSelectedUsers([]);
            setSelectedCourse("");
            setShowModal(true);
          }}
          disabled={isTableLoading}
          sx={{
            borderRadius: "50px",
            bgcolor: GOLD,
            "&:hover": { bgcolor: "#B8860B" },
          }}
        >
          Assign Course
        </Button>
      </Box>

      <Paper
        sx={{
          borderRadius: "24px",
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
        }}
      >
        <Box
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${alpha(GOLD, 0.1)}`,
          }}
        >
          <TextField
            placeholder="Search assignments..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: GOLD }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": { borderRadius: "12px" },
            }}
          />
          {isTableLoading && (
            <CircularProgress size={24} sx={{ color: GOLD }} />
          )}
        </Box>

        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            overflow: "hidden",
          }}
        >
          {isTableLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: GOLD }} />
            </Box>
          ) : (
            <TableComponent
              columns={columns}
              data={filteredAssignments}
              pagination={true}
              itemsPerPage={10}
            />
          )}
        </Box>
      </Paper>

      {/* Assignment Modal */}
      <Dialog
        open={showModal}
        onClose={() => !isModalLoading && setShowModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", p: 1 } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {editingId ? "Edit Assignment" : "Assign New Course"}
          <IconButton
            onClick={() => setShowModal(false)}
            disabled={isModalLoading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel>Filter by Occupation</InputLabel>
                <Select
                  value={selectedOccupation}
                  onChange={(e) => {
                    setSelectedOccupation(e.target.value);
                    setSelectedUsers([]);
                  }}
                  label="Filter by Occupation"
                  sx={{ borderRadius: "12px" }}
                >
                  <MenuItem value="">All Occupations</MenuItem>
                  {allOccupations.map((occ, i) => (
                    <MenuItem key={i} value={occ}>
                      {getOccupationName(occ)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 700, color: "text.secondary" }}
              >
                Select Users{" "}
                {selectedOccupation &&
                  `(${getOccupationName(selectedOccupation)})`}
              </Typography>
              <Box
                sx={{
                  maxHeight: 300,
                  overflowY: "auto",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  borderRadius: "16px",
                  p: 1,
                }}
              >
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <Box
                      key={user.user_id}
                      onClick={() => {
                        if (!isModalLoading) {
                          setSelectedUsers((prev) =>
                            prev.includes(user.user_id.toString())
                              ? prev.filter(
                                  (id) => id !== user.user_id.toString(),
                                )
                              : [...prev, user.user_id.toString()],
                          );
                        }
                      }}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        cursor: "pointer",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        transition: "0.2s all",
                        bgcolor: selectedUsers.includes(user.user_id.toString())
                          ? alpha(GOLD, 0.08)
                          : "transparent",
                        border: `1px solid ${selectedUsers.includes(user.user_id.toString()) ? GOLD : "transparent"}`,
                        "&:hover": { bgcolor: alpha(GOLD, 0.05) },
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: GOLD,
                          width: 32,
                          height: 32,
                          fontSize: "0.8rem",
                        }}
                      >
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          {user.first_name} {user.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography
                    variant="caption"
                    sx={{ p: 4, display: "block", textAlign: "center" }}
                  >
                    No users found.
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Course</InputLabel>
                <Select
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  label="Select Course"
                  sx={{ borderRadius: "12px" }}
                >
                  <MenuItem value="">Choose a course</MenuItem>
                  {courses.map((c) => (
                    <MenuItem key={c.course_id} value={c.course_id}>
                      {c.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Paper
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  bgcolor: alpha(GOLD, 0.03),
                  border: `1px dashed ${alpha(GOLD, 0.2)}`,
                }}
              >
                <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                  Selection Summary
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="caption">Users Selected:</Typography>
                  <Typography variant="caption" fontWeight={700} color={GOLD}>
                    {selectedUsers.length}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="caption">Course:</Typography>
                  <Typography variant="caption" fontWeight={700} color={GOLD}>
                    {courses.find(
                      (c) =>
                        c.course_id.toString() === selectedCourse.toString(),
                    )?.title || "None"}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setShowModal(false)}
            variant="text"
            disabled={isModalLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            variant="contained"
            disabled={
              isModalLoading || !selectedUsers.length || !selectedCourse
            }
            sx={{
              borderRadius: "50px",
              bgcolor: GOLD,
              "&:hover": { bgcolor: "#B8860B" },
              px: 4,
            }}
          >
            {isModalLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : editingId ? (
              "Update"
            ) : (
              "Assign Now"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseAssignment;
